// Redaction of patient identifiers in a Question, on the device, before any send.
// See docs/juisci-gpt-patient-data-redaction.md. No lookbehind: the iOS 15 WKWebView has none.

// The device Patterns find the first five Categories. The server pass also finds contact details (AD-20).
export const REDACTION_CATEGORIES = [
  "name_with_birth_date",
  "patient_name",
  "hospital_identifier",
  "address",
  "contact",
  "national_identifier",
] as const;
export type Category = (typeof REDACTION_CATEGORIES)[number];

export type RedactionMarkers = { readonly [C in Category]: string };
export type RedactionSummary = {
  matchCount: number;
  categories: ReadonlyArray<Category>;
};
// What the composer banner shows after a send. Null when Redaction found nothing.
export type RedactionNotice = { summary: RedactionSummary };

type Match = { category: Category; start: number; end: number };

const digitsOf = (value: string) => value.replace(/\D/g, "");

const mod97Key = (digits: string) =>
  97 - [...digits].reduce((rest, digit) => (rest * 10 + Number(digit)) % 97, 0);

// NIR (FR): 15 characters, Corsica 2A/2B, mod 97 key.
const nirValid = (raw: string) => {
  const value = raw.replace(/[ .-]/g, "").toUpperCase();
  if (value.length !== 15) return false;
  const body = value.slice(0, 13).replace("2A", "19").replace("2B", "18");
  if (!/^\d{13}$/.test(body)) return false;
  return mod97Key(body) === Number(value.slice(13));
};

// NISS (BE): YY.MM.DD-XXX.KK, mod 97 key, births from 2000 prefixed by 2.
const nissValid = (raw: string) => {
  const value = digitsOf(raw);
  if (value.length !== 11) return false;
  const month = Number(value.slice(2, 4)) % 20;
  const day = Number(value.slice(4, 6));
  if (month > 12 || day > 31) return false;
  const key = Number(value.slice(9));
  const body = value.slice(0, 9);
  return mod97Key(body) === key || mod97Key(`2${body}`) === key;
};

const ean13Valid = (digits: string) => {
  let sum = 0;
  for (let i = 0; i < 12; i++)
    sum += Number(digits.charAt(i)) * (i % 2 === 0 ? 1 : 3);
  return (10 - (sum % 10)) % 10 === Number(digits.charAt(12));
};

const luhnValid = (digits: string) => {
  let sum = 0;
  for (let i = 0; i < digits.length; i++) {
    let value = Number(digits.charAt(digits.length - 1 - i));
    if (i % 2 === 1) {
      value *= 2;
      if (value > 9) value -= 9;
    }
    sum += value;
  }
  return sum % 10 === 0;
};

const nhsValid = (digits: string) => {
  let sum = 0;
  for (let i = 0; i < 9; i++) sum += Number(digits.charAt(i)) * (10 - i);
  const key = 11 - (sum % 11);
  return key !== 10 && (key === 11 ? 0 : key) === Number(digits.charAt(9));
};

const labelAround = (text: string, start: number, end: number, label: RegExp) =>
  label.test(text.slice(Math.max(0, start - 40), start)) ||
  label.test(text.slice(end, end + 20));

const NATIONAL: ReadonlyArray<{
  regex: RegExp;
  valid: (text: string, value: string, start: number, end: number) => boolean;
}> = [
  {
    regex: /\b[12](?:[ .-]?[\dAB]){12}[ .-]?\d{2}\b/gi,
    valid: (_t, v) => nirValid(v),
  },
  {
    regex: /\b\d{2}[ .]?\d{2}[ .]?\d{2}[ .-]?\d{3}[ .]?\d{2}\b/g,
    valid: (_t, v) => nissValid(v),
  },
  // AVS (CH): 756, EAN-13 key.
  {
    regex: /\b756[ .]?\d{4}[ .]?\d{4}[ .]?\d{2}\b/g,
    valid: (_t, v) => ean13Valid(digitsOf(v)),
  },
  // NAS/SIN (CA): Luhn key, and grouped or labelled, since nine bare digits are too common.
  {
    regex: /\b[1-79]\d{2}([ -]?)\d{3}\1\d{3}\b/g,
    valid: (t, v, start, end) =>
      luhnValid(digitsOf(v)) &&
      (/[ -]/.test(v) ||
        labelAround(
          t,
          start,
          end,
          /\b(?:SIN|NAS|social insurance|assurance sociale)\b/i
        )),
  },
  // NHS (UK): mod 11 key, and space grouped or labelled.
  {
    regex: /\b\d{3}([ -]?)\d{3}\1\d{4}\b/g,
    valid: (t, v, start, end) =>
      nhsValid(digitsOf(v)) &&
      (/ /.test(v) || labelAround(t, start, end, /\bNHS\b/i)),
  },
  // SSN (US): no key, so dashed, or labelled.
  {
    regex: /\b(?!000|666|9\d\d)\d{3}-(?!00)\d{2}-(?!0000)\d{4}\b/g,
    valid: () => true,
  },
  {
    regex: /\b(?!000|666|9\d\d)\d{3} ?(?!00)\d{2} ?(?!0000)\d{4}\b/g,
    valid: (t, _v, start, end) =>
      labelAround(t, start, end, /\b(?:SSN|social security)\b/i),
  },
];

// "#" is a separator, so "Encounter #E7730418" keeps its letter; a glued value starts with a digit ("mRNA1273").
const HOSPITAL_LABEL =
  /\b(?:IPP|NIP|NDA|MRN|URN|MR(?=\s*#)|n[°o]\.?\s*(?:de\s+|d['’]\s*)?(?:dossier|s[ée]jour|patient|venue|admission|hospitalisation)|num[ée]ro\s+(?:de\s+|d['’]\s*)?(?:dossier|s[ée]jour|patient|venue|hospitalisation)|(?:dossier|s[ée]jour|venue)(?:\s+(?:n[°o]\.?|num[ée]ro|m[ée]dical|patient))?|identifiant\s+patient|ID\s+patient|med(?:ical)?\.?\s+rec(?:ord)?s?\.?(?:\s+(?:number|no\.?)|(?=\s*#))|(?:hospital|hosp\.?|patient|case|chart|unit|encounter|admission|episode|visit|account)(?:\s+(?:number|no\.?|ID)|(?=\s*#)))(?:\s*n[°o]\.?)?(?:\s*(?:#\s*:?|:)\s*|\s+|(?=\d))/gi;
const HOSPITAL_VALUE = /[A-Z0-9]+(?:(?:[-/.]|[ \t](?=[A-Z0-9]*\d))[A-Z0-9]+)*/y;
// Label after the value: "0031277408 (NDA)", "K4471203 (hospital number)".
const HOSPITAL_SUFFIX =
  /\b([A-Z]{0,3}\d[A-Z0-9]*(?:[-/.][A-Z0-9]+)*)[ \t]*\((?:IPP|NIP|NDA|MRN|URN|(?:hospital|hosp\.?|unit|patient|case|chart)\s+(?:number|no\.?|ID|#)|n[°o]\.?\s*(?:de\s+)?(?:dossier|s[ée]jour)|dossier|s[ée]jour)\)/gi;

// Case-insensitive literal without the `i` flag, so that a city or a name keeps its capital.
const anyCase = (words: ReadonlyArray<string>) =>
  `(?:${words
    .map((word) =>
      word
        .replace(/\p{Ll}/gu, (c) => `[${c}${c.toUpperCase()}]`)
        .replace(/\./g, "\\.")
        .replace(/ /g, "[ \\t]+")
    )
    .join("|")})`;

const FR_STREET_TYPE = anyCase([
  "rue",
  "avenue",
  "av.",
  "boulevard",
  "bd",
  "chemin",
  "allée",
  "allee",
  "impasse",
  "quai",
  "place",
  "route",
  "square",
  "passage",
  "chaussée",
  "chaussee",
  "faubourg",
  "sentier",
  "ruelle",
  "esplanade",
  "promenade",
  "résidence",
  "residence",
  "lotissement",
  "hameau",
  "parvis",
  "cité",
  "cite",
  "villa",
  "montée",
  "montee",
  "traverse",
  "rang",
  "côte",
  "cote",
  "ch.",
  "chem.",
  "rte",
  "rte.",
  "pl.",
  "imp.",
  "bd.",
  "boul.",
  "bvd",
  "fbg",
  "sq.",
]);
// "cours" is also a course of treatment ("2 cours de Méthotrexate"): it needs a postcode.
const FR_COURS = anyCase(["cours"]);
const FR_ARTICLE = `(?:${anyCase(["de la", "des", "du", "de", "la", "les", "le"])}[ \\t]+|${anyCase(["de l", "d", "l"])}['’])`;
const CAP_WORD = "[A-ZÀ-Þ][\\wÀ-ÿ'’-]*";
const CITY =
  "(?:St\\.?[ \\t]+)?[A-ZÀ-Þ][\\wÀ-ÿ'’-]*(?:[ \\t]+[A-ZÀ-Þ][\\wÀ-ÿ'’-]*){0,2}";
const FR_STREET_NAME = `${FR_ARTICLE}?${CAP_WORD}(?:[ \\t]+${FR_ARTICLE}?${CAP_WORD}){0,4}`;
const FR_UNIT = `(?:,?[ \\t]+${anyCase(["appartement", "appt", "app", "apt", "boîte", "boite", "bte", "bus", "bureau", "bâtiment", "batiment", "bât", "bat", "escalier", "esc", "étage", "etage", "porte", "logement", "log", "suite", "unité", "unite"])}\\.?[ \\t]*[\\w-]+)`;
// A line break is accepted only before the postcode, which anchors the tail.
const TAIL_SEP = "(?:,?[ \\t]+(?:[–-][ \\t]+)?|,?[ \\t]*\\r?\\n[ \\t]*)";
const CA_POSTCODE = "[A-Z]\\d[A-Z][ \\t]?\\d[A-Z]\\d";
// A dose is no city after a postcode: "2 cours de Pegaspargase 2500 UI/m2", "7400 MBq".
const NOT_DOSE_UNIT =
  "(?!(?:UI|IU|U|MUI|MU|UA|UFC|CFU|Gy|Bq|MBq|GBq)(?![\\wÀ-ÿ'’-]))";
const FR_POSTAL_TAIL = `(?:${TAIL_SEP}(?:(?:[A-Z]{1,2}-)?\\d{4,5}[ \\t]+${NOT_DOSE_UNIT}${CITY}(?:[ \\t]+${anyCase(["cedex"])}(?:[ \\t]+\\d{1,2})?)?|${CITY}(?:[ \\t]+\\([A-ZÀ-Þ][\\wÀ-ÿ. -]{1,24}\\)|,?[ \\t]+(?:QC|ON|BC|AB|MB|NB|NL|NS|PE|SK|Qu[ée]bec|Ontario))?,?[ \\t]+${CA_POSTCODE}))`;

// Without the abbreviation dot, which each pattern takes after `\b`: "742 W Maple Dr., Unit 3".
const EN_STREET_TYPE =
  "(?:Street|St|Road|Rd|Avenue|Ave|Drive|Dr|Lane|Ln|Boulevard|Blvd|Court|Ct|Place|Pl|Way|Close|Crescent|Cres|Terrace|Ter|Gardens|Gdns|Grove|Square|Sq|Parkway|Pkwy|Highway|Hwy|Circle|Cir|Trail|Trl|Row|Mews|Walk|Rise|Pike|Plaza|Alley)";
const UK_POSTCODE = "[A-Z]{1,2}\\d[A-Z\\d]?[ \\t]?\\d[A-Z]{2}";
const US_STATE_ZIP =
  "(?:[A-Z]{2}|[A-Z][a-z]+(?:[ \\t][A-Z][a-z]+)?)[ \\t]+\\d{5}(?:-\\d{4})?";
// A locality may precede the city: "14 Albert Road, Headingley, Leeds LS6 1AB".
const EN_POSTAL_TAIL = `(?:(?:${TAIL_SEP}${CITY}){1,2},?[ \\t]+(?:${US_STATE_ZIP}|[A-Z]{2}[ \\t]+${CA_POSTCODE}|${UK_POSTCODE}))`;
const EN_UNIT = "(?:Apt\\.?|Apartment|Unit|Flat|Suite|Ste\\.?|#)";
const EN_PO_BOX =
  "(?:P\\.?[ \\t]?O\\.?[ \\t]+Box|Post[ \\t]+Office[ \\t]+Box)[ \\t]+\\d{1,6}";
// Any case, for telegraphic notes: the postcode is then required, and the street type
// is one that names nothing else ("2 cours de chimiothérapie, 1000 mg" is a dose).
// In English the comma before the city is required too: "ln transformed values to 10000".
const ANY_CASE_WORD = "\\p{L}[\\p{L}'’-]*";
const FR_ROAD =
  "(?:rue|avenue|av\\.|boulevard|bd\\.?|boul\\.|bvd|impasse|imp\\.|all[ée]e|quai|faubourg|fbg|chauss[ée]e|ruelle|r[ée]sidence|lotissement)";
const EN_ROAD =
  "(?:street|st|road|rd|avenue|ave|boulevard|blvd|lane|ln|drive|crescent|terrace|parkway|pkwy|highway|hwy)";

const ADDRESS: ReadonlyArray<RegExp> = [
  // FR: number first.
  new RegExp(
    `\\b\\d{1,4}(?:[ \\t]?(?:bis|ter|[A-D]))?,?[ \\t]+${FR_STREET_TYPE}[ \\t]+${FR_STREET_NAME}${FR_UNIT}{0,3}${FR_POSTAL_TAIL}?`,
    "gu"
  ),
  // FR: number first, postcode required.
  new RegExp(
    `\\b\\d{1,4}(?:[ \\t]?(?:bis|ter|[A-D]))?,?[ \\t]+${FR_COURS}[ \\t]+${FR_STREET_NAME}${FR_UNIT}{0,3}${FR_POSTAL_TAIL}`,
    "gu"
  ),
  // BE/CH: street first, number after, postcode required.
  new RegExp(
    `\\b(?:${FR_STREET_TYPE}|${FR_COURS})[ \\t]+${FR_STREET_NAME},?[ \\t]+\\d{1,4}[a-z]?\\b${FR_UNIT}{0,3}${FR_POSTAL_TAIL}`,
    "gu"
  ),
  // FR in any case: number first, postcode required.
  new RegExp(
    `\\b\\d{1,4}(?:[ \\t]?(?:bis|ter))?,?[ \\t]+${FR_ROAD}[ \\t]+${ANY_CASE_WORD}(?:[ \\t]+${ANY_CASE_WORD}){0,5}${FR_UNIT}{0,3}${TAIL_SEP}(?:[a-z]{1,2}-)?\\d{4,5}[ \\t]+${ANY_CASE_WORD}(?:[ \\t]+cedex(?:[ \\t]+\\d{1,2})?)?`,
    "giu"
  ),
  // FR, BE, CH, CA: post office box, postcode required.
  new RegExp(
    `\\b(?:BP|B\\.P\\.|CP|C\\.P\\.|${anyCase(["case postale"])})[ \\t]*\\d{1,6}${FR_POSTAL_TAIL}`,
    "gu"
  ),
  // EN: optional flat first, number, capitalised name, street type, then a road number or a box.
  new RegExp(
    `(?:\\b${EN_UNIT}[ \\t]*\\w+,?[ \\t]+)?\\b(?:\\d{1,5}[–-])?\\d{1,5}[A-Za-z]?[ \\t]+(?:[A-Z][\\w'’.-]*[ \\t]+){1,4}${EN_STREET_TYPE}\\b\\.?(?:[ \\t]+\\d{1,5}[A-Z]?(?=[ \\t]*[,\\n]))?(?:,?[ \\t]+${EN_UNIT}[ \\t]*\\w+)?(?:,?[ \\t]+${EN_PO_BOX})?${EN_POSTAL_TAIL}?`,
    "gu"
  ),
  // EN in any case: postcode required.
  new RegExp(
    `\\b\\d{1,5}[a-z]?[ \\t]+(?:${ANY_CASE_WORD}[ \\t]+){1,4}${EN_ROAD}\\b\\.?(?:,?[ \\t]+${EN_UNIT}[ \\t]*\\w+)?,[ \\t]+${ANY_CASE_WORD}(?:[ \\t]+${ANY_CASE_WORD})?,?[ \\t]+(?:[a-z]{2}[ \\t]+\\d{5}(?:-\\d{4})?|${UK_POSTCODE})`,
    "giu"
  ),
  // EN: post office box, city and postcode required.
  new RegExp(`\\b${EN_PO_BOX}${EN_POSTAL_TAIL}`, "gu"),
];

const FR_MONTH =
  "(?:janvier|f[ée]vrier|mars|avril|mai|juin|juillet|ao[uû]t|septembre|octobre|novembre|d[ée]cembre|janv\\.?|f[ée]vr?\\.?|avr\\.?|juil\\.?|sept\\.?|oct\\.?|nov\\.?|d[ée]c\\.?)";
const EN_MONTH =
  "(?:January|February|March|April|May|June|July|August|September|October|November|December|Jan\\.?|Feb\\.?|Mar\\.?|Apr\\.?|Jun\\.?|Jul\\.?|Aug\\.?|Sept?\\.?|Oct\\.?|Nov\\.?|Dec\\.?)";
const DATE = `(?:\\d{1,2}[/.-]\\d{1,2}[/.-](?:\\d{4}|\\d{2})|\\d{4}-\\d{2}-\\d{2}|(?:1er|\\d{1,2})\\s+${FR_MONTH}\\s+\\d{4}|\\d{1,2}(?:st|nd|rd|th)?\\s+${EN_MONTH},?\\s+\\d{4}|${EN_MONTH}\\s+\\d{1,2}(?:st|nd|rd|th)?,?\\s+\\d{4}|\\d{1,2}[ ./-](?:${EN_MONTH}|${FR_MONTH})[ ./-]\\d{2,4})`;
const BIRTH_CUE = new RegExp(
  `(?:\\bn[ée][eé]?s?(?:\\s*\\(e\\))?\\s+le|\\bDDN|\\bdate\\s+de\\s+naiss(?:ance|\\.)|\\bnaissance|\\bborn(?:\\s+on)?|\\bDOB|\\bD\\.O\\.B\\.?|\\bdate\\s+of\\s+birth|\\bbirth\\s*date|\\bb\\.(?=\\s*\\d)|°(?=\\s*\\d))\\s*[:-]?\\s*(${DATE})`,
  "giu"
);

const CAPS_WORD = "(?:[A-ZÀ-Þ]['’])?[A-ZÀ-Þ]{2,}(?:['’-][A-ZÀ-Þ]{2,})*";
const NAME_WORD = `${CAPS_WORD}|[A-ZÀ-Þ](?:[a-zß-ÿ]+|['’](?=[A-ZÀ-Þ][a-zß-ÿ]))(?:['’-]?[A-ZÀ-Þ][a-zß-ÿ]+|-[a-zß-ÿ]+)*`;
const PARTICLES = [
  "de",
  "du",
  "van",
  "von",
  "da",
  "di",
  "le",
  "la",
  "ben",
  "bin",
  "el",
  "der",
  "den",
  "del",
  "della",
  "dos",
  "das",
] as const;
const PARTICLE_WORDS = new Set<string>(PARTICLES);
// In any of its three cases, so that a particle never counts as a name word: "POCARD DU COSQUER DE KERVILER".
const PARTICLE = `(?:${PARTICLES.flatMap((p) => [p, p.charAt(0).toUpperCase() + p.slice(1), p.toUpperCase()]).join("|")})`;
const FIELD_LABEL =
  "(?:Nom|NOM|Pr[ée]noms?|PR[ÉE]NOMS?|Name|NAME|Surname|Forename|(?:Family|Last|First|Given)[ \\t]+[Nn]ame|Patiente?)";
// A space (with a particle), or a line break followed by a field label: "LEROY\nPrénom : Martine".
const NAME_SEP = `(?:[ \\t]*(?:\\r?\\n[ \\t]*|[ \\t])${FIELD_LABEL}[ \\t]*:[ \\t]*|[ \\t]+(?:${PARTICLE}[ \\t]+){0,2})`;
// "van der Berg, Annelies": surname, comma, then one or two given names.
const SURNAME_FIRST = `(?:\\b${PARTICLE}[ \\t]+){0,2}(?:${NAME_WORD})(?:[ \\t]+(?:${NAME_WORD}))?[ \\t]*,[ \\t]*(?:${NAME_WORD})(?:[ \\t]+(?:${NAME_WORD}))?`;
// The name ends before the cue, after a comma, a bracket or a column bar: "GIRAUD Solène | ° 22/08/1964".
const NAME_BEFORE = new RegExp(
  `(${SURNAME_FIRST}|(?:${CAPS_WORD}[ \\t]*,[ \\t]*)?(?:${NAME_WORD})(?:${NAME_SEP}(?:${NAME_WORD})){0,4})[\\s,;:(|/·•–-]*$`,
  "u"
);
const LEADING_PARTICLES = new RegExp(
  `^[ \\t]*((?:${PARTICLE}[ \\t]+){1,2})$`,
  "u"
);
const NAME_WORDS = new RegExp(NAME_WORD, "gu");
const HONORIFIC =
  /^(?:Mme|Mmes|M|MM|Mr|Mrs|Ms|Miss|Mlle|Madame|Monsieur|Mademoiselle)\.?$/;
const STOP_WORDS = new Set([
  "patient",
  "patiente",
  "enfant",
  "résumé",
  "resume",
  "summary",
  "bébé",
  "bebe",
  "baby",
  "child",
  "dr",
  "pr",
  "prof",
  "le",
  "la",
  "les",
  "the",
  "dossier",
  "case",
  "nom",
  "name",
  "fillette",
  "garçon",
  "garcon",
  "nourrisson",
  "adolescent",
  "adolescente",
  "homme",
  "femme",
  "boy",
  "girl",
  "infant",
  "mother",
  "father",
  "mère",
  "père",
  // A study or a group is no name: "Étude SPRINT, 65 ans".
  "étude",
  "etude",
  "study",
  "essai",
  "trial",
  "cohorte",
  "cohort",
  "groupe",
  "group",
  "bras",
  "arm",
  // Nor a disease or a ward: "Maladie de Crohn, 24 ans", "Peds ED, 6-month-old".
  "maladie",
  "disease",
  "syndrome",
  "peds",
  "service",
  "urgences",
  "ward",
  // Nor a trial endpoint: "Overall Survival, 24 months".
  "median",
  "médiane",
  "mediane",
  "mean",
  "moyenne",
  "overall",
  "survival",
  "survie",
]);
const LABEL_WORDS = new Set([
  "nom",
  "prénom",
  "prenom",
  "prénoms",
  "prenoms",
  "name",
  "surname",
  "forename",
  "patient",
  "patiente",
]);

// Any-case names (2 or 3 words) at the start of a line or after "patient" or a title: "kevin o'brien dob 3/9/2022".
const ANCHOR =
  "(?:[Pp]atiente?|[Pp]t|[Mm]me|[Mm]lle|[Mm]adame|[Mm]onsieur|[Mm]rs?|[Mm]s|[Mm]iss|M)\\.?[ \\t]*:?[ \\t]+";
const ANY_WORD = "[A-Za-zÀ-ÿ]+(?:['’-][A-Za-zÀ-ÿ]+)*";
const ANCHORED_NAMES = `(${ANY_WORD},?[ \\t]+${ANY_WORD}(?:[ \\t]+${ANY_WORD})?)[ \\t]*[,;:(|/·•–-]?[ \\t]*$`;
const NAME_AT_LINE_START = new RegExp(`^(?:${ANCHOR})?${ANCHORED_NAMES}`, "u");
const NAME_AFTER_ANCHOR = new RegExp(
  `(?:^|[^A-Za-zÀ-ÿ])${ANCHOR}${ANCHORED_NAMES}`,
  "u"
);
const ANCHORED_STOP = new Set([
  ...STOP_WORDS,
  "un",
  "une",
  "des",
  "du",
  "de",
  "d",
  "l",
  "mon",
  "ma",
  "mes",
  "son",
  "sa",
  "ses",
  "notre",
  "nos",
  "votre",
  "leur",
  "my",
  "his",
  "her",
  "our",
  "their",
  "your",
  "a",
  "an",
  "this",
  "that",
  "ce",
  "cet",
  "cette",
  "il",
  "elle",
  "ils",
  "elles",
  "on",
  "je",
  "j",
  "nous",
  "vous",
  "he",
  "she",
  "they",
  "i",
  "we",
  "you",
  "qui",
  "who",
  "est",
  "était",
  "etait",
  "été",
  "ete",
  "suis",
  "sont",
  "is",
  "was",
  "were",
  "has",
  "had",
  "have",
  "been",
  "am",
  "et",
  "and",
  "ou",
  "or",
  "also",
  "aussi",
  "déjà",
  "deja",
  "already",
  "né",
  "née",
  "ne",
  "nee",
  "enfants",
  "fils",
  "fille",
  "filles",
  "nouveau",
  "nouveau-né",
  "nouveau-ne",
  "jumeau",
  "jumelle",
  "jumeaux",
  "twin",
  "twins",
  "children",
  "daughter",
  "newborn",
  "man",
  "woman",
  "male",
  "female",
  "prématuré",
  "prématurée",
  "premature",
  "petit",
  "petite",
  "little",
  "grand",
  "grande",
  "sujet",
  "âgé",
  "âgée",
  "age",
  "agé",
  "agée",
  "jeune",
  "old",
  "young",
  "elderly",
  "adult",
  "adulte",
  "senior",
  "known",
  "obese",
  "obèse",
  "diabétique",
  "diabetique",
  "diabetic",
  "smoker",
  "fumeur",
  "term",
  "preterm",
  "healthy",
  "sain",
  "saine",
  "mr",
  "mrs",
  "ms",
  "miss",
  "mme",
  "mlle",
  "madame",
  "monsieur",
  "pt",
]);

// A name in any case, only when nothing but a title or a line start can precede it.
const findAnchoredName = (
  text: string,
  cueStart: number,
  anchor: "lineStartOrTitled" | "titled"
): number | null => {
  const lineStart = text.lastIndexOf("\n", cueStart - 1) + 1;
  const windowStart = Math.max(lineStart, cueStart - 80);
  const window = text.slice(windowStart, cueStart);
  const atLineStart =
    anchor === "lineStartOrTitled" && windowStart === lineStart
      ? NAME_AT_LINE_START.exec(window)
      : null;
  const match =
    atLineStart === null ? NAME_AFTER_ANCHOR.exec(window) : atLineStart;
  if (match === null) return null;
  const names = match[1];
  if (names === undefined)
    throw new Error("should not happen: the name group is mandatory");
  if (names.split(/[ \t,]+/).some((w) => ANCHORED_STOP.has(w.toLowerCase())))
    return null;
  return windowStart + match.index + match[0].lastIndexOf(names);
};

// A capitalised name; a single word needs a title, since one capitalised word is too often a drug or a trial.
const findCapitalisedName = (text: string, cueStart: number): number | null => {
  const prefixStart = Math.max(0, cueStart - 80);
  const nameMatch = NAME_BEFORE.exec(text.slice(prefixStart, cueStart));
  if (nameMatch === null) return null;
  const names = nameMatch[1];
  if (names === undefined)
    throw new Error("should not happen: the name group is mandatory");
  const words = [...names.matchAll(NAME_WORDS)];
  let honorific = false;
  let skipped = 0;
  for (const word of words) {
    if (HONORIFIC.test(word[0])) honorific = true;
    else if (!STOP_WORDS.has(word[0].toLowerCase())) break;
    skipped += 1;
  }
  const kept = words.slice(skipped);
  const first = kept.at(0);
  if (first === undefined) return null;
  const nameWords = kept.filter((w) => !LABEL_WORDS.has(w[0].toLowerCase()));
  if (nameWords.length === 0) return null;
  const previous = skipped === 0 ? null : words[skipped - 1];
  if (previous === undefined)
    throw new Error("should not happen: a skipped word exists");
  const leadStart =
    previous === null
      ? 0
      : HONORIFIC.test(previous[0])
        ? previous.index + previous[0].length
        : null;
  const particles =
    leadStart === null
      ? null
      : LEADING_PARTICLES.exec(names.slice(leadStart, first.index));
  const particleGroup = particles === null ? "" : particles[1];
  if (particleGroup === undefined)
    throw new Error("should not happen: the particle group is mandatory");
  const offset =
    prefixStart + nameMatch.index + first.index - particleGroup.length;
  const textBefore = text.slice(Math.max(0, offset - 12), offset);
  if (
    nameWords.length === 1 &&
    !honorific &&
    !/\b(?:Mme|M\.|Mr|Mrs|Ms|Miss|Mlle|Madame|Monsieur)\.?\s*$/.test(textBefore)
  )
    return null;
  return offset;
};

// An age after a name, with an optional "est un homme de", "is a", "âgé de": "Jean Martin, 48 ans", "John Smith is a 48-year-old".
const AGE_CUE = new RegExp(
  `(?:(?:\\b(?:is|was|est|etait)|était)[ \\t]+(?:(?:an?|une?)[ \\t]+|(?=(?:[âa]g[ée]e?[ \\t]+de|aged)[ \\t]|\\d{1,3}(?:[ \\t]*(?:ans?|yo)\\b|[ \\t]*y\\.?[/.]?o\\b|-(?:year|month)s?-old\\b|[ \\t]*(?:years?|yrs?|months?)[ \\t-]old\\b))))?(?:\\b(?:homme|femme|patiente?|enfant|garcon|garçon|fille|man|woman|male|female|boy|girl)[ \\t]+(?:de|of|aged)?[ \\t]*)?(?:[âa]g[ée]e?[ \\t]+de[ \\t]+|\\baged?[ \\t]*:?[ \\t]*)?\\b\\d{1,3}(?:[ \\t]*(?:ans?|years?(?:[ \\t-]old)?|yrs?|yo|mois|months?(?:[ \\t-]old)?)\\b|[ \\t]*y\\.?[/.]?o\\b\\.?|-(?:year|month)s?-old\\b)`,
  "giu"
);
// A title, then one to four name words, particles aside: "Mme Durand", "M. Jean-Paul MARTIN". "M" needs its dot ("pic M", "M Protein").
const HONORIFIC_NAME = new RegExp(
  `(?:^|[^\\p{L}])(?:Mme|Mmes|Mlle|Madame|Monsieur|Mademoiselle|Mrs|Mr|Ms|Miss|M\\.)\\.?[ \\t]+((?:${PARTICLE}[ \\t]+){0,2}(?:${NAME_WORD})(?:[ \\t]+(?:${PARTICLE}[ \\t]+){0,2}(?:${NAME_WORD})){0,3})(?![\\p{L}-]|['’](?![sS](?!\\p{L}))\\p{L})`,
  "gu"
);
// Words that no surname holds, unlike stop words such as "Ward" or "Petit".
const ENDPOINT_WORDS = new Set([
  "survival",
  "survie",
  "disease",
  "maladie",
  "syndrome",
  "study",
  "étude",
  "etude",
  "trial",
  "essai",
  "cohort",
  "cohorte",
  "median",
  "médiane",
  "mediane",
  "overall",
  "progression",
  "response",
  "réponse",
  "reponse",
  "group",
  "groupe",
  // "arm" but not "bras", a French surname.
  "arm",
  "control",
  "contrôle",
  "controle",
  "placebo",
]);
const NAME_TRAIL = /[\s,;:(|/·•–-]+$/u;

// A name with no birth date: before an age, or after a title. The age stays, since it is clinical.
const findPatientNames = (text: string): Array<Match> => {
  const found: Array<Match> = [];
  for (const cue of text.matchAll(AGE_CUE)) {
    const capitalised = findCapitalisedName(text, cue.index);
    // In any case, only after a title or "patient": a line start is too often a symptom ("Douleur thoracique, 48 ans").
    const start =
      capitalised === null
        ? findAnchoredName(text, cue.index, "titled")
        : capitalised;
    if (start === null) continue;
    // An endpoint word anywhere marks a study term, not a name: "Progression-Free Survival, 2 years".
    const words = text
      .slice(start, cue.index)
      .split(/[\s,;:(|/·•–-]+/)
      .map((w) => w.toLowerCase());
    if (words.some((w) => ENDPOINT_WORDS.has(w))) continue;
    const end =
      start + text.slice(start, cue.index).replace(NAME_TRAIL, "").length;
    found.push({ category: "patient_name", start, end });
  }
  for (const m of text.matchAll(HONORIFIC_NAME)) {
    const names = m[1];
    if (names === undefined)
      throw new Error("should not happen: the name group is mandatory");
    // A particle opens a surname ("Mme Le Roux"): the stop word check reads the word after it.
    const first = names
      .split(/[ \t]+/)
      .find((w) => !PARTICLE_WORDS.has(w.toLowerCase()));
    // Particles alone are the surname ("Mr Das").
    const checked = first === undefined ? names : first;
    if (STOP_WORDS.has(checked.toLowerCase())) continue;
    const start = m.index + m[0].length - names.length;
    found.push({ category: "patient_name", start, end: start + names.length });
  }
  return found;
};

const findNameBirthDates = (text: string): Array<Match> => {
  const found: Array<Match> = [];
  for (const cue of text.matchAll(BIRTH_CUE)) {
    const cueStart = cue.index;
    let end = cueStart + cue[0].length;
    const capitalised = findCapitalisedName(text, cueStart);
    const start =
      capitalised === null
        ? findAnchoredName(text, cueStart, "lineStartOrTitled")
        : capitalised;
    if (start === null) continue;
    if (text.slice(start, cueStart).includes("(") && text.charAt(end) === ")")
      end += 1;
    found.push({ category: "name_with_birth_date", start, end });
  }
  return found;
};

// Sorted, non-overlapping spans: an overlap of the same Category extends the earlier span,
// and one of another Category keeps the part past it, which is patient data too.
// The spans point at raw patient data: never store, log or send them.
export const findMatches = (text: string): ReadonlyArray<Match> => {
  const all: Array<Match> = [
    ...findNameBirthDates(text),
    ...findPatientNames(text),
  ];
  for (const { regex, valid } of NATIONAL) {
    for (const m of text.matchAll(regex)) {
      const end = m.index + m[0].length;
      if (valid(text, m[0], m.index, end))
        all.push({ category: "national_identifier", start: m.index, end });
    }
  }
  for (const label of text.matchAll(HOSPITAL_LABEL)) {
    HOSPITAL_VALUE.lastIndex = label.index + label[0].length;
    const value = HOSPITAL_VALUE.exec(text);
    if (value === null) continue;
    if (digitsOf(value[0]).length < 4) continue;
    // A US FDA application number ("NDA 215256") has exactly six digits.
    if (/^NDA\b/i.test(label[0]) && /^\d{6}$/.test(value[0])) continue;
    all.push({
      category: "hospital_identifier",
      start: value.index,
      end: value.index + value[0].length,
    });
  }
  for (const m of text.matchAll(HOSPITAL_SUFFIX)) {
    const value = m[1];
    if (value === undefined)
      throw new Error("should not happen: the value group is mandatory");
    if (digitsOf(value).length < 4) continue;
    all.push({
      category: "hospital_identifier",
      start: m.index,
      end: m.index + value.length,
    });
  }
  for (const regex of ADDRESS) {
    for (const m of text.matchAll(regex)) {
      all.push({
        category: "address",
        start: m.index,
        end: m.index + m[0].replace(/[\s,.;:]+$/, "").length,
      });
    }
  }
  all.sort((a, b) => (a.start === b.start ? b.end - a.end : a.start - b.start));
  const kept: Array<Match> = [];
  for (const m of all) {
    const last = kept.at(-1);
    if (last === undefined || m.start >= last.end) {
      kept.push({ ...m });
      continue;
    }
    if (m.end <= last.end) continue;
    if (m.category === last.category) {
      last.end = m.end;
      continue;
    }
    // "…75011 Paris, DUPONT Jean né le…": the name starts inside the address.
    const skip = text.slice(last.end, m.end).search(/[^\s,;:]/);
    if (skip === -1) continue;
    kept.push({ category: m.category, start: last.end + skip, end: m.end });
  }
  return kept;
};

const summarise = (
  categories: ReadonlyArray<Category>,
  matchCount: number
): RedactionSummary | null =>
  matchCount === 0
    ? null
    : {
        matchCount,
        categories: REDACTION_CATEGORIES.filter((c) => categories.includes(c)),
      };

// The Notice of a send once the server pass answers: its Matches add to the device ones.
export const withServerSummary = (
  notice: RedactionNotice | null,
  server: RedactionSummary | null
): RedactionNotice | null => {
  if (server === null) return notice;
  const summary = summarise(
    notice === null
      ? server.categories
      : [...notice.summary.categories, ...server.categories],
    (notice === null ? 0 : notice.summary.matchCount) + server.matchCount
  );
  if (summary === null)
    throw new Error("should not happen: a server summary without a Match");
  return { summary };
};

// The only text an assistant send accepts: a private constructor makes the type nominal.
export class Redaction {
  readonly #text: string;
  readonly #notice: RedactionNotice | null;

  private constructor(text: string, notice: RedactionNotice | null) {
    this.#text = text;
    this.#notice = notice;
  }

  get text(): string {
    return this.#text;
  }

  get notice(): RedactionNotice | null {
    return this.#notice;
  }

  static run(question: string, markers: RedactionMarkers): Redaction {
    const matches = findMatches(question);
    let text = "";
    let cursor = 0;
    for (const match of matches) {
      text += question.slice(cursor, match.start) + markers[match.category];
      cursor = match.end;
    }
    const summary = summarise(
      matches.map((m) => m.category),
      matches.length
    );
    return new Redaction(
      text + question.slice(cursor),
      summary === null ? null : { summary }
    );
  }

  // A Question redacted on another screen: run again, a no-op by INV-08, and keep what that screen found.
  static handedOver(
    question: string,
    notice: RedactionNotice | null,
    markers: RedactionMarkers
  ): Redaction {
    const again = Redaction.run(question, markers);
    if (notice === null) return again;
    if (again.#notice === null) return new Redaction(again.#text, notice);
    const summary = summarise(
      [...notice.summary.categories, ...again.#notice.summary.categories],
      notice.summary.matchCount + again.#notice.summary.matchCount
    );
    if (summary === null)
      throw new Error("should not happen: two Notices sum to no Match");
    return new Redaction(again.#text, { summary });
  }

  // When the kill switch turns Redaction off, or for a question to another engine (AD-13).
  static unchecked(question: string): Redaction {
    return new Redaction(question, null);
  }
}

export type QuestionSegment =
  { kind: "text"; value: string } | { kind: "marker"; value: string };

// Splits a sent Question at the Markers of one language, so the bubble can style them (AD-18).
export const markerSegments = (
  text: string,
  markers: RedactionMarkers
): ReadonlyArray<QuestionSegment> => {
  const labels = REDACTION_CATEGORIES.map((c) => markers[c]);
  // An empty label matches at every position, so the scan would never advance.
  if (labels.includes(""))
    throw new Error("should not happen: a Redaction Marker is empty");
  const segments: Array<QuestionSegment> = [];
  let cursor = 0;
  for (;;) {
    let start = -1;
    let label = "";
    for (const candidate of labels) {
      const at = text.indexOf(candidate, cursor);
      if (at === -1) continue;
      if (
        start === -1 ||
        at < start ||
        (at === start && candidate.length > label.length)
      ) {
        start = at;
        label = candidate;
      }
    }
    if (start === -1) break;
    if (start > cursor)
      segments.push({ kind: "text", value: text.slice(cursor, start) });
    segments.push({ kind: "marker", value: label });
    cursor = start + label.length;
  }
  if (cursor < text.length)
    segments.push({ kind: "text", value: text.slice(cursor) });
  return segments;
};
